// TopBar — single global scope affordance (PSD §10.1). Presentation only.
//
// Distilled (CRITIQUE.md Priority Issue 5): the bar promotes ONE scope signal —
// an `entity › valuation` breadcrumb — and relocates Disclaimer + Load Demo
// into a secondary overflow menu so the primary surface can breathe. LockMenu
// and the status indicator remain but carry calmer visual weight.

import { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, MoreVertical, Info, Database } from 'lucide-react';
import { useStore, useActiveValuation, useSettings } from '../../store/selectors';
import { LockMenu } from '../AppLock';
import { DISCLOSURE } from './disclaimer';
import { LoadDemoModal } from './LoadDemoModal';

export function TopBar() {
  const valuations = useStore((s) => s.valuations);
  const activeId = useStore((s) => s.activeValuationId);
  const setActive = useStore((s) => s.setActiveValuation);
  const settings = useSettings();
  const setActiveEntity = useStore((s) => s.setActiveEntity);
  const active = useActiveValuation();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showLoadDemo, setShowLoadDemo] = useState(false);

  const entityNames = Array.from(new Set(Object.values(valuations).map((v) => v.entityName)));

  return (
    <header className="topbar app-topbar">
      <div className="sidebar-brand topbar-brand">Backsolve</div>

      {/* Primary scope: a single `entity › valuation` breadcrumb. The two
          selectors are the breadcrumb segments — entity is demoted (muted),
          valuation is the primary node — so users form one stable "what am I
          looking at" model instead of three competing scope signals. */}
      <nav className="topbar-scope" aria-label="Active scope">
        <span className="scope-select-wrap scope-entity">
          <select
            className="scope-select"
            value={settings.activeEntityName ?? ''}
            onChange={(e) => setActiveEntity(e.target.value || null)}
            aria-label="Active entity"
          >
            <option value="">No entity</option>
            {entityNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <ChevronDown className="scope-select-caret" aria-hidden="true" />
        </span>
        <ChevronRight className="scope-sep" aria-hidden="true" />
        <span className="scope-select-wrap scope-valuation">
          <select
            className="scope-select"
            value={activeId ?? ''}
            onChange={(e) => setActive(e.target.value || null)}
            aria-label="Active valuation"
          >
            <option value="">No valuation</option>
            {Object.values(valuations).map((v) => (
              <option key={v.valuationId} value={v.valuationId}>
                {v.entityName} — {v.status}
              </option>
            ))}
          </select>
          <ChevronDown className="scope-select-caret" aria-hidden="true" />
        </span>
      </nav>

      {/* Calm status indicator — quiet dot + label instead of a loud pill. */}
      {active && <span className="topbar-status">{active.status}</span>}

      {/* Secondary overflow menu — Disclaimer + Load Demo live here, off the
          primary bar. The unicode `ⓘ` glyph is replaced by a real lucide icon. */}
      <OverflowMenu
        showLoadDemo={settings.showLoadDemo}
        onDisclaimer={() => setShowDisclaimer(true)}
        onLoadDemo={() => setShowLoadDemo(true)}
      />

      <LockMenu />
      <LoadDemoModal open={showLoadDemo} onClose={() => setShowLoadDemo(false)} />

      {showDisclaimer && (
        <div
          className="modal-overlay"
          style={modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Independent-appraisal disclaimer"
          onClick={() => setShowDisclaimer(false)}
        >
          <div className="modal" style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h3>Independent-Appraisal Disclaimer</h3>
            <p style={{ fontSize: 'var(--font-size-2)', lineHeight: 1.6 }}>{DISCLOSURE}</p>
            <button type="button" className="btn btn-secondary" onClick={() => setShowDisclaimer(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

/** Lightweight secondary menu for relocated actions (Disclaimer, Load Demo). */
function OverflowMenu({
  showLoadDemo,
  onDisclaimer,
  onLoadDemo,
}: {
  showLoadDemo: boolean;
  onDisclaimer: () => void;
  onLoadDemo: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="topbar-overflow" ref={ref}>
      <button
        type="button"
        className="topbar-overflow-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="topbar-overflow-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="topbar-overflow-item"
            onClick={() => {
              setOpen(false);
              onDisclaimer();
            }}
          >
            <Info size={15} /> Disclaimer
          </button>
          {showLoadDemo && (
            <button
              type="button"
              role="menuitem"
              className="topbar-overflow-item"
              onClick={() => {
                setOpen(false);
                onLoadDemo();
              }}
            >
              <Database size={15} /> Load demo
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalBox: React.CSSProperties = {
  background: 'var(--color-surface-raised)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-3)',
  padding: 'var(--space-5)',
  maxWidth: 560,
  boxShadow: 'var(--shadow-3)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};
